/**
 * Prop Description:
 * name => **required** name of the animation data json file
 * isCommon => bool, which will define if the json is coming from common folder or not.
 * size => this will maintain the 1:1 for the image ie. (height and width will be equals to size).
 * height, width => use this prop, if the the ratio of height and width is not 1:1.
 * style => custom html style object which will be applied on lottieContainer div.
 * className => custom className (this will also apply on the container div).
 * onClick => click handler
 * numberOfLoops => use prop to control number of animation loops
 */

import React, { useEffect, useMemo, useRef, useState } from "react";
import LottieLib from "lottie-react";
import { getConfig } from "../../../utils/functions";
import PropTypes from "prop-types";
import { noop } from "lodash-es";
import { Skeleton } from "@mui/material";

const Lottie = ({
  size,
  width,
  height,
  style,
  className,
  name,
  isCommon,
  onClick,
  dataAid,
  numberOfLoops,
  ...restProps
}) => {
  const lottieWidth = size || width;
  const lottieHeight = size || height;
  const [animationData, setAnimationData] = useState("");
  const [loop, setLoop] = useState(true);
  const { productName } = useMemo(getConfig, []);
  const lottieRef = useRef();

  useEffect(() => {
    try {
      const animationData = isCommon
        ? require(`assets/lottie/${name}.json`)
        : require(`assets/${productName}/lottie/${name}.json`);
      setAnimationData(animationData);
    } catch (err) {
      throw new Error(err);
    }
  }, [productName, name]);

  useEffect(() => {
    if (!numberOfLoops || !animationData) return;
    const animationDuration = lottieRef?.current?.getDuration();
    const animationTimeout = setTimeout(() => {
      setLoop(false);
    }, animationDuration * (numberOfLoops - 1) * 1000);
    return () => {
      clearTimeout(animationTimeout);
    };
  }, [animationData]);

  if (!animationData) {
    return (
      <Skeleton
        className={className}
        style={{ borderRadius: "8px", ...style }}
        variant="rectangular"
        width={lottieHeight}
        height={lottieWidth}
      />
    );
  }

  return (
    <LottieLib
      autoPlay
      loop={loop}
      lottieRef={lottieRef}
      animationData={animationData}
      className={className}
      style={{
        height: lottieHeight,
        width: lottieWidth,
        ...style,
      }}
      onClick={onClick}
      data-aid={`iv_${dataAid}`}
      {...restProps}
    />
  );
};

export default Lottie;

Lottie.defaultProps = {
  style: {},
  onClick: noop,
  size: "250px",
  numberOfLoops: 0,
};

Lottie.propTypes = {
  size: PropTypes.string,
  width: PropTypes.string,
  height: PropTypes.string,
  style: PropTypes.object,
  className: PropTypes.string,
  name: PropTypes.string.isRequired,
  isCommon: PropTypes.bool,
  onClick: PropTypes.func,
  numberOfLoops: PropTypes.number,
};
